import React, { forwardRef, useId } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  wrapperClassName?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, id, wrapperClassName, error, className, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id || generatedId;

    return (
      <div className={wrapperClassName}>
        {label && (
          <label 
            htmlFor={inputId} 
            className="mb-2 ml-1 block text-[11px] font-black uppercase tracking-[0.15em] text-gray-500 dark:text-slate-400"
          >
            {label}
          </label>
        )}
        <div className="relative group">
          <input
            ref={ref}
            id={inputId}
            className={`
              block w-full px-4 py-3.5 
              bg-white text-gray-900 font-semibold text-sm dark:bg-slate-900 dark:text-slate-100
              border-2 rounded-xl shadow-sm
              placeholder-gray-400 dark:placeholder-slate-500
              transition-all duration-200
              focus:outline-none focus:ring-0
              disabled:bg-gray-50 disabled:text-gray-400 dark:disabled:bg-slate-800/80 dark:disabled:text-slate-500
              ${error 
                ? 'border-red-200 focus:border-red-500 bg-red-50/30 dark:border-red-900/60 dark:bg-red-950/20' 
                : 'border-gray-100 hover:border-gray-200 focus:border-indigo-600 focus:shadow-md focus:shadow-indigo-50 dark:border-slate-700 dark:hover:border-slate-500 dark:focus:border-sky-500 dark:focus:shadow-sky-950/20'
              } 
              ${className || ''}
            `}
            aria-invalid={error ? 'true' : 'false'}
            {...props}
          />
          {error && (
            <div className="flex items-center gap-1.5 mt-2 ml-1">
              <svg className="w-3.5 h-3.5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-[11px] font-bold uppercase tracking-wide text-red-500 dark:text-red-300" role="alert">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

Input.displayName = 'Input';
